import type { DataFlag, Receipt, SignedStep, SigningPayload } from "../api/types.js";
import { debitSource, describeStep, protectiveOnly, signedHold, sourceShown } from "./describe.js";
import { calendarDate, dateTime, formatMoney, sumMoney } from "../lib/format.js";

/**
 * The sentences a bank card adds for a policy decision (UX spec v3 §6.5, §6.6,
 * copy §8.1) — composed from the SIGNED payload, the bank's own reads and the
 * gateway's receipt, never written by the assistant and never from chat state.
 * Each is a bold lead plus the rest of the line, as the notice draws them.
 */
export interface Line {
  readonly lead: string;
  readonly rest: string;
}

const TOKEN_REST = " · after you sign, confirm with your security token";

/** DCTA-176 (§F.2 26b): the warning the bank signs while the identity check is due — payments keep working. */
export const KYC_DUE_WARNING = "WARN-KYC-REFRESH-DUE";
export const KYC_DUE_REST = " Payments keep working until then.";

/**
 * 26b: the date the customer's identity check is due by, as SIGNED on a step beside WARN-KYC-REFRESH-DUE
 * (`policy.kyc_update_by`, B59) — "30 Sep 2027". No signed date, no line: a date the bank did not sign is never printed.
 */
export function kycDueDate(steps: readonly SignedStep[]): string | undefined {
  const step = steps.find((s) => (s.policy.warnings ?? []).includes(KYC_DUE_WARNING) && s.policy.kyc_update_by !== undefined);
  const date = step?.policy.kyc_update_by;
  return date === undefined ? undefined : calendarDate(date);
}

/** 26b: "Your identity check is due by {date}." · "Payments keep working until then." — Sign stays active. */
export function kycDueLine(steps: readonly SignedStep[]): Line | undefined {
  const date = kycDueDate(steps);
  return date === undefined ? undefined : { lead: `Your identity check is due by ${date}.`, rest: KYC_DUE_REST };
}

/**
 * 05 warn: one line before the signature for any step the policy sends to the
 * token. The figure is the one the bank SIGNED on the step (`policy.step_up`,
 * ask t): the rule that tripped and its amount — the daily allowance
 * (POL-LIMIT-DAILY) or the per-payment threshold (POL-STEPUP-THRESHOLD). Read
 * from the payload itself, so the line cannot disagree with the rule; an older
 * payload with no limit keeps the words and drops the figure.
 */
export function tokenLine(steps: readonly SignedStep[]): Line | undefined {
  // DCTA-232: a protective action is no payment — its token step is said on its own row (the bank's unlock sentence).
  const stepped = steps.find((s) => s.policy.outcome === "step_up_required" && s.type !== "protective_action");
  if (stepped === undefined) return undefined;
  // DCTA-154 (Claude's Q23 ruling; UX spec Q23): the limit is named, never its figure — a coached customer reading out
  // "S$5,000" can be asked for S$4,999 to skip the token (DCTA-153 ruling 3's reasoning). The figure is on Limits (21u).
  const daily = stepped.policy.step_up?.rule_id === "POL-LIMIT-DAILY";
  // DCTA-162 F28 (§F.2 21z, DCTA-155 round 2): the one-payment form is "A payment this size" — ‘Above your transfer limit’
  // named something the Limits page never shows; its help line now names the threshold (21u).
  return { lead: daily ? "Above your daily transfer limit" : "A payment this size", rest: TOKEN_REST };
}

/**
 * The account a step takes money from. DCTA-143: `flags` — the bank's flags beside the payload — so a flagged account
 * name is said in its inert form here too ("Nothing left “…”."), as the step's row prints it.
 */
function debited(step: SignedStep, flags: readonly DataFlag[] = []): { id: string; name: string } | undefined {
  // A sell takes nothing from an account.
  const src = step.type === "securities_order" && step.side !== "buy" ? undefined : debitSource(step);
  return src === undefined ? undefined : { id: src.account.account_id, name: sourceShown(step, flags) ?? src.account.display_name };
}

function moneyOf(step: SignedStep): { value: string; currency: string } | undefined {
  if (step.type === "securities_order") return step.cash_amount;
  if (step.type === "protective_action" || step.type === "payee_add") return undefined;
  return step.amount;
}

/**
 * 05 after the signature: what the code releases (the steps that have not run),
 * the account those steps would take from, and whether anything already went —
 * so no sentence claims "nothing left" of an account a step already debited.
 */
export function stepUpCopy(payload: SigningPayload, receipt: Receipt, flags: readonly DataFlag[] = []) {
  // DCTA-232: the bank's unlock card — no money moves: no figure, no payment's words.
  const moneyless = protectiveOnly(payload.steps);
  const unlocking = moneyless && unlockOnly(payload.steps);
  const stateOf = (s: SignedStep): string | undefined =>
    receipt.steps.find((r) => (r.step_id ?? r.index) === s.step_id || r.index === s.index)?.state;
  const ran = payload.steps.filter((s) => stateOf(s) === "executed");
  const waiting = payload.steps.filter((s) => stateOf(s) !== "executed");
  const amounts = waiting.map(moneyOf).filter((m): m is { value: string; currency: string } => m !== undefined);
  const currency = amounts[0]?.currency ?? "SGD";
  const amount = formatMoney(sumMoney(amounts.map((m) => m.value)), currency);
  const touched = new Set(ran.map((s) => debited(s, flags)).filter((a) => a !== undefined).map((a) => a.id));
  const sources = [...new Map(waiting.map((s) => debited(s, flags)).filter((a) => a !== undefined).map((a) => [a.id, a])).values()];
  const only = sources.length === 1 ? sources[0] : undefined;
  const untouched = only !== undefined && !touched.has(only.id) ? only.name : undefined;
  const partial = ran.length > 0;
  const first = waiting[0];
  // DCTA-209: a schedule card's step carries no amount the schedule pays as such (a card bill: the bill on the day of
  // signing) — its cancel names the payments, as the stop sheet does ("Stop the payments to Mum?").
  const schedule = payload.schedule;
  const what =
    waiting.length !== 1 || first === undefined
      ? `the ${amount} still waiting`
      : schedule !== undefined
        ? `the ${schedule.cadence === "once" ? "payment" : "payments"} to ${describeStep(first, flags).name}`
        : first.type === "securities_order"
          ? `the ${amount} order for ${describeStep(first, flags).name}`
          : `the ${amount} to ${describeStep(first, flags).name}`;
  return {
    amount: moneyless ? "" : amount,
    untouched,
    partial,
    /** `by` is the bank's clock time ("by 21:56") or, before the bank has said its time, its window ("within 15 minutes"). */
    // Coordinator, 14 Oct: the line says what the code does — the slogan's last variant is out (test/no-slogan.spec.ts).
    waiting: (by: string): Line => ({ lead: "Waiting for your security token.", rest: moneyless ? ` Enter the code ${by}.` : ` Enter the code ${by} to send the payment.` }),
    cancelAsk: (moneyless
      ? { lead: unlocking ? "Cancel unlocking your money?" : "Cancel it?", rest: unlocking ? STAYS_LOCKED : " Nothing changes." }
      : {
          lead: `Cancel ${what}?`,
          rest: partial ? " Nothing more will leave your account." : " Nothing has left your account, and nothing will.",
        }) as Line,
    cancelled: (at: string | undefined): Line => ({
      lead: at === undefined ? "Cancelled." : `Cancelled at ${at}.`,
      rest: `${untouched === undefined ? "" : ` Nothing left ${untouched}.`} Say it again if you change your mind.`,
    }),
    exhausted: {
      lead: "Cancelled after three codes didn't match.",
      rest: `${untouched === undefined ? "" : ` Nothing left ${untouched}.`} Say it again when you have your token.`,
    } as Line,
    expired: (by: string): Line => ({
      lead: moneyless ? `No code ${by}, so nothing changed.` : partial ? `No code ${by}, so the rest was not sent.` : `No code ${by}, so nothing was sent.`,
      rest: `${untouched === undefined ? "" : ` ${untouched} is untouched.`} Say it again when you have your token.`,
    }),
    /** 05 rm-callback: the call is the second factor, on the same window as the code. */
    calling: {
      lead: "We'll call you on your registered number",
      rest: moneyless ? ", usually within 15 minutes." : ", usually within 15 minutes. It goes out after the call.",
    } as Line,
    noCall: (by: string): Line => ({
      lead: moneyless ? `No call ${by}, so nothing changed.` : partial ? `No call ${by}, so the rest was not sent.` : `No call ${by}, so nothing was sent.`,
      rest: moneyless ? " Say it again when you have your token." : `${untouched === undefined ? "" : ` ${untouched} is untouched.`} Say it again if you still want to send it.`,
    }),
  };
}

const COOLING = "POL-COOLING-NEWPAYEE";

/** DCTA-153: the scam check also holds this step (the weights reached the hold band) — asked AND held. */
function scamHeld(step: SignedStep): boolean {
  return signedHold(step) !== undefined && step.policy.rule_ids.includes("POL-SCAM-INTERVENTION");
}

/** The wait's hours as the bank SIGNED them on the step (B13, B14) — a whole number, or none (none is invented). */
function signedHours(step: SignedStep): string | undefined {
  const h = step.policy.cooling_hours;
  return h !== undefined && /^[1-9]\d*$/.test(h) ? h : undefined;
}

/** "the 12-hour wait" — with the signed hours (§F.2 12g), never a constant; "the wait" when a payload signed none. */
function theWait(step: SignedStep): string {
  const h = signedHours(step);
  return h === undefined ? "the wait" : `the ${h}-hour wait`;
}

/** A first payment to a new payee, held until its wait ends (POL-COOLING-NEWPAYEE with a hold, DCTA-119). */
export function isCooling(step: SignedStep): boolean {
  return step.type === "funds_transfer" && step.payee?.is_new_payee === true && step.policy.rule_ids.includes(COOLING);
}

/**
 * DCTA-124 (§F.2 13d, B14): the add's wait, before the signature — as a duration, never a clock time:
 * the wait starts at the signature, which the card cannot know (decision 17). The hours are the ones
 * the bank SIGNED on the step; with none, no sentence (no number is invented).
 */
export function addWaitLine(step: SignedStep): Line | undefined {
  const h = step.type === "payee_add" ? signedHours(step) : undefined;
  return h === undefined ? undefined : { lead: `Payments to them can go out ${h} hours after you sign.`, rest: " Every new payee has this wait." };
}

/**
 * 06 before the signature: a held step says when it goes out and that it can be
 * cancelled before then, so the signature covers a known wait. The new payee's
 * cooling hold has the deck's own sentence; any other hold (the market is shut,
 * a large outflow) gets the deck's generic wait line, with no reason invented.
 */
export function heldLine(step: SignedStep, flags: readonly DataFlag[] = []): Line | undefined {
  const until = signedHold(step);
  // DCTA-232: a protective action's wait is said on its own row (the bank's unlock sentence), never as money going out.
  if (until === undefined || step.type === "protective_action") return undefined;
  const m = moneyOf(step);
  const amount = m === undefined ? "" : formatMoney(m.value, m.currency);
  const when = dateTime(until);
  if (isCooling(step)) {
    return {
      // DCTA-143: a flagged nickname is said in its inert form, as the row prints it.
      lead: `${describeStep(step, flags).name} is a new payee`,
      // §F.2 21j (DCTA-155, F25): scam-held too, `hold_until` is the later of the two holds — naming the wait could name
      // the wrong one, so the clause goes. QA D4 (DCTA-159's copy note, for ux-designer to confirm): one release time, and
      // both reasons named — true whichever hold is the later.
      rest: scamHeld(step)
        ? ` and this payment is held for a safety check, so the ${amount} goes out on ${when}. You can cancel it any time before then.`
        : `, so the ${amount} goes out on ${when}, when ${theWait(step)} ends. You can cancel it any time before then.`,
    };
  }
  if (isMarketClosed(step)) {
    // DCTA-75 P1-6: the customer's first question about a hold is why — here it is one clause.
    return { lead: "The market is closed", rest: `, so the ${amount} order goes out on ${when}, when it opens. You can cancel it any time before then.` };
  }
  return { lead: `The ${amount} goes out on ${when}.`, rest: " You can cancel it any time before then." };
}

/** The bank's own key for a closed market (the register's warning, or the rule that set the hold). */
function isMarketClosed(step: SignedStep): boolean {
  return (step.policy.warnings ?? []).includes("WARN-MARKET-CLOSED") || step.policy.rule_ids.includes("POL-MARKET-CLOSED");
}

/**
 * 11, a held receipt: WHY it waits (the deck's receipt line). The sum line and the
 * row already say when it goes out, and "Cancel the held …" is the way to stop it,
 * so only a reason earns the notice — a new payee's; a market hold has no sentence
 * in the deck (README deviations, item 8), and none is written.
 */
export function heldReason(step: SignedStep): string | undefined {
  return isCooling(step) ? "New payee. Held so you can cancel it before it goes out." : undefined;
}

/** 06 after the signature: the held card's own sentences (held · cancel · cancelled). `flags`: DCTA-143, as above. */
export function heldCopy(steps: readonly SignedStep[], flags: readonly DataFlag[] = []) {
  const amounts = steps.map(moneyOf).filter((m): m is { value: string; currency: string } => m !== undefined);
  const amount = formatMoney(sumMoney(amounts.map((m) => m.value)), amounts[0]?.currency ?? "SGD");
  const sources = [...new Set(steps.map((s) => debited(s, flags)).filter((a) => a !== undefined).map((a) => a.name))];
  const account = sources.length === 1 ? sources[0] : undefined;
  const until = steps.map((s) => s.policy.hold_until).filter((u): u is string => u !== undefined).sort()[0];
  const first = steps[0];
  const what =
    steps.length !== 1 || first === undefined
      ? `the ${amount} still held`
      : first.type === "securities_order"
        ? `the ${amount} order for ${describeStep(first, flags).name}`
        : `the ${amount} to ${describeStep(first, flags).name}`;
  // §F.2 21j's reason after the signature too: beside a scam hold, the wait is not named (it may be the other hold).
  const cooling = steps.length > 0 && steps.every((s) => isCooling(s) && !scamHeld(s));
  // One wait named only when every held step signed the same one (a mix names no number).
  const waits = new Set(steps.map(theWait));
  const wait = waits.size === 1 ? [...waits][0] : "the wait";
  // DCTA-232: the bank's unlock card, held for its wait — the deck's words (mock-up 19), never a figure.
  if (protectiveOnly(steps)) {
    const unlocking = unlockOnly(steps);
    return {
      amount: "",
      held: unlocking && until !== undefined ? { lead: `Money can go out again from ${dateTime(until)}.`, rest: " Until then your money stays locked." } : ({ lead: until === undefined ? "Held" : `Held until ${dateTime(until)}`, rest: "." } as Line),
      cancelAsk: { lead: unlocking ? "Cancel unlocking your money?" : "Cancel it?", rest: unlocking ? STAYS_LOCKED : " Nothing changes." } as Line,
      cancelled: (at: string | undefined): Line => ({ lead: at === undefined ? "Cancelled." : `Cancelled at ${at}.`, rest: " Say it again if you change your mind." }),
    };
  }
  return {
    amount,
    held: {
      lead: until === undefined ? "Held" : `Goes out ${dateTime(until)}`,
      rest: `${cooling ? `, when ${wait} for a new payee ends.` : "."}${account === undefined ? "" : ` Nothing has left ${account} yet.`}`,
    } as Line,
    cancelAsk: { lead: `Cancel ${what}?`, rest: " Nothing has left your account, and nothing will." } as Line,
    cancelled: (at: string | undefined): Line => ({
      lead: at === undefined ? "Cancelled." : `Cancelled at ${at}.`,
      rest: `${account === undefined ? "" : ` Nothing left ${account}.`} Say it again if you change your mind.`,
    }),
  };
}

const STAYS_LOCKED = " Your money stays locked.";

/** DCTA-232: every step unlocks the money (the bank's unlock card). */
function unlockOnly(steps: readonly SignedStep[]): boolean {
  return steps.every((s) => s.type === "protective_action" && s.action === "unlock_funds");
}

/** DCTA-232: a cancel names what it stops — a transfer, or the unlock (a protective card is no transfer). */
export function cancelLabel(steps: readonly SignedStep[]): string {
  if (!protectiveOnly(steps)) return "Cancel this transfer";
  return unlockOnly(steps) ? "Cancel unlocking" : "Cancel this change";
}
